import React, { useState, useEffect, useContext, Fragment, useCallback } from 'react';
import { readApplications } from './../../../common/api/products';
import { Application } from 'appDtos';
import { ApplicationCard } from './application-card/application-card';
import { ConfigContext, AuthContext, Header, Footer, LoadingIndicator, Sizes, IEBannerWarning, useIsSingleUi } from '@chr/navisphereonline-shared-components';
import { userInGroup, validUserIdInToken, clearUserData, clearStoredJwt, loadAppConfig } from '@chr/online-shared-microsite-core';
import { getWindowLocationHost, windowLocationAssign } from '../../../common/utility/window-helpers';
import './home.scss';
import { OnlineTranslationContext } from '@chr/online-shared-ui-localizations';
import { Banner, Typography } from "@chr/eds-react";

interface HomeProps {
  translate: (key: string, replaceVariable?: string[]) => string;
}

interface ApplicationsState {
  current: Application[];
  isLoading: boolean;
}

const HomeComponent: React.FC<HomeProps> = ({ translate }) => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const configContext = useContext(ConfigContext);
  const [applications, setApplications] = useState<ApplicationsState>({
    current: [],
    isLoading: true
  });

  const isSingleUi = useIsSingleUi();

  const translateText = useCallback((key: string, replaceVariable?: string[]) => {
    return translate ? translate(key, replaceVariable) : key;
  }, [translate]);

  const visionAppDescriptor = translateText('Access real-time, global supply chain visibility for your freight across all carriers.');

  useEffect(() => {
    const fetchApplications = async () => {
      const applications: Application[] = (validUserIdInToken()) ? await readApplications() : [];
      const config = await loadAppConfig() as AppConfig;
      if (userInGroup(config.customerPlatformVisionGroupName)) {
        const vision: Application = {
          name: 'Vision',
          id: 99,
          applicationId: 99,
          description: 'Access real-time, global supply chain visibility for your freight across all carriers.',
          publicDescription: visionAppDescriptor,
          isEnabledForUser: true,
          isVisible: true,
          isEnabled: true,
          url: config.visionUri,
          sortOrder: 98
        };

        applications.push(vision);
      }

      const applicationsEnabledForUser = applications.filter(
        application => application.isEnabledForUser
      );

      applications.forEach(function (application) {
        const applicationUrl = application.url;
        let path = applicationUrl;
        if (config.online2Uri === applicationUrl) {
          path = isSingleUi ? applicationUrl + '/homepage/' : applicationUrl + '/#/dashboard';
        }
        else if (config.online1Uri === applicationUrl + '/login.aspx') {
          path = applicationUrl + '/login.aspx';
        }
        application.url = path;
      });

      if (applicationsEnabledForUser && applicationsEnabledForUser.length === 1) {
        clearStorageOnDomainChange(applicationsEnabledForUser[0].url);
        windowLocationAssign(applicationsEnabledForUser[0].url);
        return;
      }

      const online2Enabled = applications.filter(
        application => application.id === 1 && application.isEnabledForUser
      );

      if ((online2Enabled.length === 1 && localStorage.getItem('newSession') !== null)) {
        setIsRedirecting(true);
        localStorage.removeItem('newSession');
        clearStorageOnDomainChange(online2Enabled[0].url);
        windowLocationAssign(online2Enabled[0].url);
        return;
      }

      applications.sort((a, b) => {
        if (!a.sortOrder) return 1; // if sortOrder is null, put it at the end 
        if (!b.sortOrder) return -1;
        return a.sortOrder - b.sortOrder;
      });
      setApplications({ current: applications, isLoading: false });
    };

    fetchApplications();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearStorageOnDomainChange = (appUrl: string) => {
    if (appUrl.indexOf(getWindowLocationHost()) === -1) {
      clearUserData();
      clearStoredJwt();
    }
  };

  const applicationEnabledButAllApplicationsAreNotEnabledForUser = () => (
    <p data-testid="disabledApplications">
      {translateText("Oops... you don't seem to be set up with access to any applications.")}
      <br />
      {translateText("Please contact your C.H. Robinson representative for assistance.")}
    </p>
  );

  const NoEnabledApplicationsAtUserLevel = () => {
    return applicationEnabledButAllApplicationsAreNotEnabledForUser();
  }

  const NoEnabledApplicationsAtAppLevel = () => (
    <p data-testid="noEnabledApplications">
      {translateText("We are unable to provide links to the applications you have access to.")}
      <br />
      {translateText("Please contact your C.H. Robinson representative for assistance.")}
    </p>
  );

  const multipleApplications = (applications: Application[]) => {
    return (
      <Fragment>
        <p>
          {translateText("Choose an application")}
        </p>
        <div className="customer-application-container space-outer-top-lg space-inner-top-md">
          {applications.map(application => {
            return (
              <ApplicationCard
                key={application.applicationId}
                application={application}
                onOpenApp={clearStorageOnDomainChange}
              />
            );
          })}
        </div>
      </Fragment>
    );
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh'
        }}
        data-testid="loading"
      >
        <LoadingIndicator size={Sizes.Medium} />
      </div>
    );
  };

  const displayContent = (applications: Application[]) => {
    const noApplicationEnabledAtUserLevel: boolean = applications.length === 0;
    const noApplicationEnabledAtAppLevel: boolean = applications.filter(application => application.isEnabled).length === 0;
    const notEnabledApplications: Application[] = applications.filter(
      application => application.isEnabled && application.isEnabledForUser === false
    );

    const enabledApplications: Application[] = applications.filter(
      application => application.isEnabled && application.isEnabledForUser
    );

    if (noApplicationEnabledAtUserLevel) {
      return NoEnabledApplicationsAtUserLevel();
    } else if (noApplicationEnabledAtAppLevel) {
      return NoEnabledApplicationsAtAppLevel();
    } else if (applications.length === notEnabledApplications.length) {
      return applicationEnabledButAllApplicationsAreNotEnabledForUser();
    } else {
      return multipleApplications(enabledApplications);
    }
  };

  if (applications.isLoading || isRedirecting) {
    return renderLoadingIndicator();
  }

  return (
    <React.Fragment>
      <AuthContext.Consumer>
        {(authClient) => <Header appConfig={{ ...configContext, allowGuests: false }} authClient={authClient} />}
      </AuthContext.Consumer>
      <div className="container banner space-outer-bottom-lg">
        <Banner title="The ordering and descriptions of this application list have been recently updated to enhance its relevance for you." severity='info' />
      </div>
      <div className="container">
        <IEBannerWarning />
        <div className="row" id="home" data-testid="home">
          <div className="col-xs-12">
            <Typography variant={'heading200'} id="welcome" fontWeight={'semiBold'} color={'#108de0'}>
              {translateText("Welcome")}
            </Typography>
            {displayContent(applications.current)}
          </div>
        </div>
      </div>
      <Footer />
    </React.Fragment>
  );
};

const Home: React.FC = () => {
  return (
    <OnlineTranslationContext.Consumer>
      {({ translate }) => (
        <HomeComponent translate={translate} />
      )}
    </OnlineTranslationContext.Consumer>
  );
};

export default Home;
